import React from 'react'
import classNames from 'classnames'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faGripVertical } from '@fortawesome/pro-solid-svg-icons'
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ActionButton from 'uiComponents/buttons'
import { Checkbox } from 'uiComponents/input/checkbox'
import './index.scss'
import { useDispatch } from 'react-redux'
import { updateTableColumns } from 'orders/orders/redux'
import { OrderTableEventHandlerContext } from 'orders/orders/orderTableEventHandler'
import { ClickAwayListener } from '@mui/base'
import { Popover } from '@mui/material'
import { useAppSelector } from 'store/hooks'
import {
    ORDERS_TABLE_COLUMNS,
    ORDERS_TABLE_COLUMNS_CONFIG,
    getColumnName,
    getSectionLabel,
    sanitizeColumns,
} from './config'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { Body } from 'uiComponents/typography'
import { useTranslation } from 'translations'

export interface ColumnConfig {
    name: string
    key: string
    selected: boolean
    sectionKey: number
    features?: string[]
}

interface SortableColumnRowProps {
    column: ColumnConfig
    onRowSelected(column: ColumnConfig): void
}

const SortableColumnRow = ({ column, onRowSelected }: SortableColumnRowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.key })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    return (
        <div
            className={classNames({ 'column-config-row': true })}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            <div className="left">
                <Checkbox checked={column.selected} onChange={() => onRowSelected(column)} />
                <Body>{getColumnName(column)}</Body>
            </div>
            <FontAwesomeIcon className="drag-icon" icon={faGripVertical as IconProp} />
        </div>
    )
}

const ColumnsConfigurator = ({ accountSlug }: { accountSlug: string }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const hasFeature = useHasFeatureFunc()
    const [tabIndex, setTabIndex] = React.useState(0)
    const { eventEmmitter } = React.useContext(OrderTableEventHandlerContext)
    const tableColumns = sanitizeColumns(
        useAppSelector((state) => state.orderTable.tableColumns),
        hasFeature,
    )
    const toggleHideAllColumns = () => eventEmmitter.emit('toggleHideAllColumns', null)
    const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null)
    const [columns, setColumns] = React.useState(tableColumns)

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    const previousAccountSlug = React.useRef(accountSlug).current
    React.useEffect(() => {
        if (accountSlug !== previousAccountSlug) {
            const newColumns = sanitizeColumns(ORDERS_TABLE_COLUMNS, hasFeature)
            setColumns(newColumns)
            dispatch(updateTableColumns(newColumns))
        }
    }, [accountSlug, hasFeature])

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setPopoverAnchor(event.currentTarget)
    }

    const handleClose = () => {
        setPopoverAnchor(null)
    }

    const handleTabsChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue)
    }

    const onRowSelected = (column: ColumnConfig) => {
        const newColumns = columns.map((col) => {
            if (col.key === column.key) {
                return { ...col, selected: !col.selected }
            }
            return col
        })
        setColumns(newColumns)
    }

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const oldIndex = columns.findIndex((c) => c.key === String(active.id))
        const newIndex = columns.findIndex((c) => c.key === String(over.id))
        if (oldIndex === -1 || newIndex === -1) return

        const newColumns = Array.from(columns)
        const [removed] = newColumns.splice(oldIndex, 1)
        newColumns.splice(newIndex, 0, removed)
        setColumns(newColumns)
    }

    const onApply = () => {
        dispatch(updateTableColumns(columns))
        toggleHideAllColumns()
        handleClose()
    }

    const renderPanel = (
        section: { section: string; columns: { name: string; key: string }[] },
        sectionIndex: number,
    ) => {
        const sectionColumns = columns.filter((column) => column.sectionKey === sectionIndex)

        return (
            <div
                id={`simple-tabpanel-${sectionIndex}`}
                key={section.section}
                className="tab-panel"
                hidden={tabIndex !== sectionIndex}
            >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext
                        items={sectionColumns.map((c) => c.key)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sectionColumns.map((column) => (
                            <SortableColumnRow key={column.key} column={column} onRowSelected={onRowSelected} />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>
        )
    }

    const everyColumnSelected = columns.every((col) => col.selected)
    const selectAll = () => {
        setColumns(
            columns.map((col) => ({
                ...col,
                selected: true,
            })),
        )
    }
    const clearAll = () => {
        setColumns(
            columns.map((col) => ({
                ...col,
                selected: false,
            })),
        )
    }

    return (
        <div>
            <ActionButton onClick={handleClick} size="medium" secondary kind="settings" className="action-button">
                <FontAwesomeIcon icon={faCog as IconProp} className="cog-icon" />
                {t('orders.orders.ordersTable.adjustView')}
            </ActionButton>
            <Popover
                id="column-configurator-popover"
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <div className="popup-container">
                        <Tabs value={tabIndex} onChange={handleTabsChange} centered>
                            {ORDERS_TABLE_COLUMNS_CONFIG.map((section) => (
                                <Tab label={getSectionLabel(section.section)} key={section.section} />
                            ))}
                        </Tabs>
                        <div className="content">
                            {ORDERS_TABLE_COLUMNS_CONFIG.map((section, sectionIndex) =>
                                renderPanel(section, sectionIndex),
                            )}
                        </div>
                        <div className="footer">
                            <ActionButton onClick={onApply} size="medium" kind="action">
                                {t('common.apply')}
                            </ActionButton>
                            {everyColumnSelected ? (
                                <ActionButton
                                    onClick={clearAll}
                                    size="medium"
                                    secondary
                                    kind="action"
                                    className="clear-button"
                                >
                                    {t('orders.orders.ordersTable.clearAll')}
                                </ActionButton>
                            ) : (
                                <ActionButton
                                    onClick={selectAll}
                                    size="medium"
                                    secondary
                                    kind="action"
                                    className="clear-button"
                                >
                                    {t('orders.orders.ordersTable.selectAll')}
                                </ActionButton>
                            )}
                        </div>
                    </div>
                </ClickAwayListener>
            </Popover>
        </div>
    )
}

export default ColumnsConfigurator
